
/**
 * Tournament adapters that translate tournament-specific data structures
 * to common interfaces for maximum code reuse
 */

import { BaseMatch, BaseResult, TournamentAdapter, TournamentConfig } from '@/types/shared-tournament';
import { ExtendedTournamentState } from '@/contexts/tournament/types';
import { CourtResult } from '@/types/tournament';

class MexicanoTournamentAdapter implements TournamentAdapter {
  constructor(
    private tournamentState: ExtendedTournamentState,
    private submitRoundResults: (results: CourtResult[]) => void
  ) {}

  getMatches(): BaseMatch[] {
    return this.tournamentState.courts.map((court, index) => ({
      court: index,
      team1: {
        player1: { id: court[0]?.id || '', name: court[0]?.name || '-' },
        player2: { id: court[1]?.id || '', name: court[1]?.name || '-' }
      },
      team2: {
        player1: { id: court[2]?.id || '', name: court[2]?.name || '-' },
        player2: { id: court[3]?.id || '', name: court[3]?.name || '-' }
      },
      score: this.getExistingScore(index)
    }));
  }

  private getExistingScore(courtIndex: number): { team1: number; team2: number } | undefined {
    const currentRoundResults = this.tournamentState.results[this.tournamentState.currentRound - 1];
    if (currentRoundResults && currentRoundResults[courtIndex]) {
      const result = currentRoundResults[courtIndex];
      return {
        team1: result.score[0],
        team2: result.score[1]
      };
    }
    return undefined;
  }

  getResults(round: number): BaseResult[] {
    const results = this.tournamentState.results[round - 1] || [];
    return results.map((result, index) => ({
      court: index,
      winner: result.winner,
      score: result.score,
      selectedTeam: "team1",
      // Include player information if available from stored round data
      players: this.getRoundPlayerInfo(round - 1, index)
    }));
  }

  private getRoundPlayerInfo(roundIndex: number, courtIndex: number) {
    const roundData = this.tournamentState.roundCourts[roundIndex];
    if (roundData && roundData.results && roundData.results[courtIndex]) {
      return roundData.results[courtIndex].players;
    }
    
    // Fallback to current court assignments if no stored player info
    if (roundData && roundData.courts && roundData.courts[courtIndex]) {
      const court = roundData.courts[courtIndex];
      return {
        team1: {
          player1: court[0]?.name || '',
          player2: court[1]?.name || ''
        },
        team2: {
          player1: court[2]?.name || '',
          player2: court[3]?.name || ''
        }
      };
    }

    return undefined;
  }

  getTotalPoints(): number {
    const currentRoundResults = this.tournamentState.results[this.tournamentState.currentRound - 1];
    if (currentRoundResults && currentRoundResults[0]?.score) {
      return currentRoundResults[0].score[0] + currentRoundResults[0].score[1];
    }
    return 24; // Default points for Mexicano
  }

  setResults(results: BaseResult[]): void {
    // This would be handled by the shared hook
  }

  restoreResults(round: number): BaseResult[] | null {
    if (round <= 0 || round > this.tournamentState.totalRounds) {
      return null;
    }
    
    const roundData = this.tournamentState.roundCourts[round - 1];
    if (!roundData || !roundData.results || roundData.results.length === 0) {
      return null;
    }

    console.log(`MexicanoAdapter: Restoring results for round ${round}:`, roundData.results);
    
    return roundData.results.map((result, index) => ({
      court: index,
      winner: result.winner,
      score: result.score,
      selectedTeam: "team1",
      players: result.players
    }));
  }

  submitResults(results: BaseResult[]): void {
    const courtResults: CourtResult[] = results.map(result => ({
      court: result.court,
      winner: result.winner,
      score: result.score
    }));
    this.submitRoundResults(courtResults);
  }

  canModifyPoints(round: number): boolean {
    return round === 1; // Only allow points modification in round 1 for Mexicano
  }

  getConfig(): TournamentConfig {
    return {
      type: 'mexicano',
      allowPointsSelection: true,
      defaultPoints: 24,
      pointsOptions: [11, 21, 24, 32],
      roundSelectionEnabled: true
    };
  }
}

class AmericanoTournamentAdapter implements TournamentAdapter {
  constructor(
    private tournamentState: ExtendedTournamentState,
    private finishAmericanoRound: (results: any[]) => void
  ) {}

  getMatches(): BaseMatch[] {
    return this.tournamentState.americanoMatches.map((match, index) => ({
      court: index,
      team1: {
        player1: { id: match.team1.player1.id, name: match.team1.player1.name },
        player2: { id: match.team1.player2.id, name: match.team1.player2.name }
      },
      team2: {
        player1: { id: match.team2.player1.id, name: match.team2.player1.name },
        player2: { id: match.team2.player2.id, name: match.team2.player2.name }
      },
      score: match.score
    }));
  }

  getResults(round: number): BaseResult[] {
    const results = this.tournamentState.americanoRoundResults[round - 1] || [];
    return results.map((match, index) => ({
      court: index,
      winner: match.score && match.score.team1 > match.score.team2 ? "team1" : "team2",
      score: match.score ? [match.score.team1, match.score.team2] : [0, 0],
      selectedTeam: "team1",
      // Include player information from the match
      players: {
        team1: {
          player1: match.team1.player1.name,
          player2: match.team1.player2.name
        },
        team2: {
          player1: match.team2.player1.name,
          player2: match.team2.player2.name
        }
      }
    }));
  }

  getTotalPoints(): number {
    const firstMatch = this.tournamentState.americanoMatches[0];
    if (firstMatch?.score) {
      return firstMatch.score.team1 + firstMatch.score.team2;
    }
    return 24; // Default points for Americano
  }

  setResults(results: BaseResult[]): void {
    // This would be handled by the shared hook
  }

  restoreResults(round: number): BaseResult[] | null {
    if (round <= 0 || round > this.tournamentState.totalRounds) {
      return null;
    }
    
    const roundData = this.tournamentState.roundAmericanoMatches[round - 1];
    if (!roundData || !roundData.results || roundData.results.length === 0) {
      return null;
    }

    console.log(`AmericanoAdapter: Restoring results for round ${round}:`, roundData.results);
    
    return roundData.results.map((match, index) => ({
      court: index,
      winner: match.score && match.score.team1 > match.score.team2 ? "team1" : "team2",
      score: match.score ? [match.score.team1, match.score.team2] : [0, 0],
      selectedTeam: "team1",
      players: match.players
    }));
  }

  submitResults(results: BaseResult[]): void {
    const americanoResults = this.tournamentState.americanoMatches.map((match, index) => {
      const result = results[index];
      return {
        ...match,
        score: {
          team1: result.score[0],
          team2: result.score[1]
        }
      };
    });
    this.finishAmericanoRound(americanoResults);
  }

  canModifyPoints(round: number): boolean {
    return round === 1; // Only allow points modification in round 1 for Americano
  }

  getConfig(): TournamentConfig {
    return {
      type: 'americano',
      allowPointsSelection: true,
      defaultPoints: 24,
      pointsOptions: [11, 21, 24, 32],
      roundSelectionEnabled: false // Americano handles rounds differently
    };
  }
}

class TeamAmericanoTournamentAdapter implements TournamentAdapter {
  constructor(
    private tournamentState: ExtendedTournamentState,
    private finishAmericanoRound: (results: any[]) => void
  ) {}

  getMatches(): BaseMatch[] {
    return this.tournamentState.americanoMatches.map((match, index) => {
      // Find team names from fixed teams
      const teamIndex1 = this.findTeamIndex(match.team1.player1.id, match.team1.player2.id);
      const teamIndex2 = this.findTeamIndex(match.team2.player1.id, match.team2.player2.id);
      
      return {
        court: index,
        team1: {
          player1: { id: match.team1.player1.id, name: match.team1.player1.name },
          player2: { id: match.team1.player2.id, name: match.team1.player2.name },
          teamName: `Team ${teamIndex1 + 1}`
        },
        team2: {
          player1: { id: match.team2.player1.id, name: match.team2.player1.name },
          player2: { id: match.team2.player2.id, name: match.team2.player2.name },
          teamName: `Team ${teamIndex2 + 1}`
        },
        score: match.score
      };
    });
  }

  private findTeamIndex(player1Id: string, player2Id: string): number {
    const teams = this.tournamentState.teamAmericanoFixedTeams || [];
    return teams.findIndex(team =>
      (team.player1.id === player1Id && team.player2.id === player2Id) ||
      (team.player1.id === player2Id && team.player2.id === player1Id)
    );
  }

  getResults(round: number): BaseResult[] {
    const results = this.tournamentState.americanoRoundResults[round - 1] || [];
    return results.map((match, index) => ({
      court: index,
      winner: match.score && match.score.team1 > match.score.team2 ? "team1" : "team2",
      score: match.score ? [match.score.team1, match.score.team2] : [0, 0],
      selectedTeam: "team1",
      players: {
        team1: {
          player1: match.team1.player1.name,
          player2: match.team1.player2.name
        },
        team2: {
          player1: match.team2.player1.name,
          player2: match.team2.player2.name
        }
      }
    }));
  }

  getTotalPoints(): number {
    const firstMatch = this.tournamentState.americanoMatches[0];
    if (firstMatch?.score) {
      return firstMatch.score.team1 + firstMatch.score.team2;
    }
    return 24;
  }

  setResults(results: BaseResult[]): void {
    // Handled by the shared hook
  }

  restoreResults(round: number): BaseResult[] | null {
    if (round <= 0 || round > this.tournamentState.totalRounds) {
      return null;
    }
    
    const roundData = this.tournamentState.roundAmericanoMatches[round - 1];
    if (!roundData || !roundData.results || roundData.results.length === 0) {
      return null;
    }

    return roundData.results.map((match, index) => ({
      court: index,
      winner: match.score && match.score.team1 > match.score.team2 ? "team1" : "team2",
      score: match.score ? [match.score.team1, match.score.team2] : [0, 0],
      selectedTeam: "team1",
      players: match.players
    }));
  }

  submitResults(results: BaseResult[]): void {
    const americanoResults = this.tournamentState.americanoMatches.map((match, index) => {
      const result = results[index];
      return {
        ...match,
        score: {
          team1: result.score[0],
          team2: result.score[1]
        }
      };
    });
    this.finishAmericanoRound(americanoResults);
  }

  canModifyPoints(round: number): boolean {
    return round === 1;
  }

  getConfig(): TournamentConfig {
    return {
      type: 'team_americano',
      allowPointsSelection: true,
      defaultPoints: 24,
      pointsOptions: [11, 21, 24, 32],
      roundSelectionEnabled: false
    };
  }
}

// Export the adapters with the correct names
export const MexicanoAdapter = MexicanoTournamentAdapter;
export const AmericanoAdapter = AmericanoTournamentAdapter;
export const TeamAmericanoAdapter = TeamAmericanoTournamentAdapter;
